import React, { useState, Component } from 'react';
import { ArrowLeftIcon, SearchIcon, PackageIcon, WarehouseIcon, MapPinIcon, ClipboardListIcon, XIcon, CheckIcon, ScanLineIcon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
interface StorageOrderOverviewProps {
  onBack: () => void;
  onOrderSelect: (orderId: string) => void;
}
interface OrderComponent {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  location: string;
  position: string;
}
interface Order {
  id: string;
  name: string;
  status: 'planned' | 'in-progress' | 'completed';
  components: OrderComponent[];
}
export function StorageOrderOverview({
  onBack,
  onOrderSelect
}: StorageOrderOverviewProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<OrderComponent | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  // Neue States für den Scan-Prozess
  const [showScanModal, setShowScanModal] = useState(false);
  const [scannedCode, setScannedCode] = useState('');
  const [scanSuccess, setScanSuccess] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  // Beispieldaten für Aufträge mit Komponenten im Lager
  const orders: Order[] = [{
    id: '1001',
    name: 'Gustoria Paella',
    status: 'in-progress',
    components: [{
      id: 'R061307',
      name: 'MSC Alaska Seelachs',
      quantity: 200,
      unit: 'kg',
      location: 'TKL-A1-01',
      position: 'T2-H3'
    }, {
      id: 'R053408',
      name: 'Hähnchen Brust St gebraten',
      quantity: 150,
      unit: 'kg',
      location: 'TKL-A2-03',
      position: 'T1-H5'
    }, {
      id: 'R090533',
      name: 'Erbsen',
      quantity: 100,
      unit: 'kg',
      location: 'TKL-B1-02',
      position: 'T3-H7'
    }]
  }, {
    id: '1002',
    name: 'Gustoria Risotto',
    status: 'planned',
    components: [{
      id: 'R053408',
      name: 'Hähnchen Brust St gebraten',
      quantity: 120,
      unit: 'kg',
      location: 'TKL-A2-03',
      position: 'T1-H6'
    }, {
      id: 'R092616',
      name: 'Zwiebeln',
      quantity: 50,
      unit: 'kg',
      location: 'TKL-B3-05',
      position: 'T4-H2'
    }]
  }];
  // Filtern der Aufträge nach Suchbegriff
  const filteredOrders = orders.filter(order => order.name.toLowerCase().includes(searchTerm.toLowerCase()) || order.id.includes(searchTerm) || order.components.some(comp => comp.name.toLowerCase().includes(searchTerm.toLowerCase()) || comp.id.includes(searchTerm)));
  // Funktion zum Anzeigen des Lagerplatz-Modals
  const handleShowLocation = (component: OrderComponent, orderId: string) => {
    setSelectedComponent(component);
    setSelectedOrderId(orderId);
    setShowLocationModal(true);
  };
  // Funktion zum Starten des Scan-Prozesses
  const handleStartScan = () => {
    setShowLocationModal(false);
    setShowScanModal(true);
    setScannedCode('');
    setScanSuccess(null);
    setErrorMessage('');
    setShowSuccessMessage(false);
  };
  // Funktion zum Scannen und Validieren
  const handleScan = () => {
    if (!scannedCode) {
      setErrorMessage('Bitte geben Sie einen Barcode ein oder scannen Sie ihn');
      setScanSuccess(false);
      return;
    }
    setIsScanning(true);
    // Simuliere den Scanvorgang mit einer kurzen Verzögerung
    setTimeout(() => {
      // Validiere, ob der gescannte Code mit der Material-ID oder Lagerplatz übereinstimmt
      const isValid = selectedComponent && (scannedCode.includes(selectedComponent.id) || scannedCode.includes(selectedComponent.location) ||
      // Für Testzwecke auch einfach die ersten 3 Zeichen akzeptieren
      scannedCode.substring(0, 3) === selectedComponent.id.substring(0, 3));
      setScanSuccess(isValid);
      if (isValid) {
        setErrorMessage('');
        setShowSuccessMessage(true);
        // Nach erfolgreicher Validierung kurz warten und dann zurück zur Übersicht
        setTimeout(() => {
          // Schließe alle Modals und setze die States zurück
          setShowScanModal(false);
          setSelectedComponent(null);
          setSelectedOrderId(null);
          setShowSuccessMessage(false);
          // Hier keine Weiterleitung mehr mit onOrderSelect, stattdessen zurück zur Übersicht
        }, 2000);
      } else {
        setErrorMessage('Der gescannte Code stimmt nicht mit dem Material oder Lagerplatz überein. Bitte überprüfen Sie den Barcode.');
      }
      setIsScanning(false);
    }, 1000);
  };
  // Funktion zum Schließen des Modals
  const handleCloseModal = () => {
    setShowLocationModal(false);
    setSelectedComponent(null);
    setSelectedOrderId(null);
  };
  // Funktion zum Schließen des Scan-Modals
  const handleCloseScanModal = () => {
    setShowScanModal(false);
    setScannedCode('');
    setScanSuccess(null);
    setErrorMessage('');
    setShowSuccessMessage(false);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleScan();
    }
  };
  // Hilfsfunktion zum Rendern des Lagerplatz-Rasters mit verbesserter Kennzeichnung
  const renderStorageGrid = (position: string) => {
    // Extrahiere Tiefe und Höhe aus der Position (z.B. "T2-H3")
    const [depthPart, heightPart] = position.split('-');
    const depth = parseInt(depthPart.substring(1));
    const height = parseInt(heightPart.substring(1));
    // Erstelle ein 5x10 Raster (5 Tiefen, 10 Höhen)
    const rows = 5;
    const cols = 10;
    return <div className="mt-4" data-id="element-1888">
        {/* Überschrift für die Höhenachse */}
        <div className="flex mb-1 pl-8" data-id="element-1889">
          {Array.from({
          length: cols
        }).map((_, colIndex) => <div key={`header-${colIndex}`} className="w-6 flex items-center justify-center text-xs font-medium text-amber-700" data-id="element-1890">
              H{cols - colIndex}
            </div>)}
        </div>
        <div className="flex" data-id="element-1891">
          {/* Beschriftung für die Tiefenachse */}
          <div className="flex flex-col mr-2" data-id="element-1892">
            {Array.from({
            length: rows
          }).map((_, rowIndex) => <div key={`row-label-${rowIndex}`} className="h-6 w-6 flex items-center justify-center text-xs font-medium text-amber-700" data-id="element-1893">
                T{rowIndex + 1}
              </div>)}
          </div>
          {/* Das eigentliche Raster */}
          <div className="grid grid-rows-5 gap-1" data-id="element-1894">
            {Array.from({
            length: rows
          }).map((_, rowIndex) => <div key={`row-${rowIndex}`} className="flex gap-1" data-id="element-1895">
                {Array.from({
              length: cols
            }).map((_, colIndex) => {
              // Prüfe, ob diese Zelle die Position des Artikels darstellt
              const isTargetPosition = rowIndex === depth - 1 && colIndex === cols - height;
              return <div key={`cell-${rowIndex}-${colIndex}`} className={`
                        w-6 h-6 border rounded-sm flex items-center justify-center text-xs
                        ${isTargetPosition ? 'bg-amber-500 text-white border-amber-600 ring-2 ring-amber-300' : 'bg-gray-100 border-gray-300'}
                      `} data-id="element-1896">
                      {isTargetPosition && <PackageIcon size={12} data-id="element-1897" />}
                    </div>;
            })}
              </div>)}
          </div>
        </div>
        {/* Koordinatenlegende */}
        <div className="mt-2 bg-amber-50 p-2 rounded-md border border-amber-200 text-center" data-id="element-1898">
          <span className="text-xs font-medium text-amber-800" data-id="element-1899">
            Material Position: {position} (Tiefe {depth}, Höhe {height})
          </span>
        </div>
      </div>;
  };
  return <div className="bg-white rounded-xl shadow-md p-6" data-id="element-1900">
      <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6" data-id="element-1901">
        <ArrowLeftIcon size={16} data-id="element-1902" />
        <span data-id="element-1903">Zurück zur Auswahl</span>
      </button>
      <div className="mb-6" data-id="element-1904">
        <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-1905">
          Aufträge mit Komponenten im TK-Lager
        </h3>
        <p className="text-gray-600 mb-4" data-id="element-1906">
          Wählen Sie einen Auftrag aus, um Komponenten auszulagern
        </p>
        {/* Suchfeld */}
        <div className="relative mb-6" data-id="element-1907">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1908">
            <SearchIcon size={18} className="text-gray-400" data-id="element-1909" />
          </div>
          <input type="text" className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md" placeholder="Nach Auftrag oder Komponente suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1910" />
        </div>
        {/* Liste der Aufträge */}
        {filteredOrders.length > 0 ? <div className="space-y-6" data-id="element-1911">
            {filteredOrders.map(order => <div key={order.id} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1912">
                <div className="bg-gray-50 p-4 flex justify-between items-center" data-id="element-1913">
                  <div data-id="element-1914">
                    <h4 className="text-lg font-semibold text-gray-800" data-id="element-1915">
                      {order.name}
                    </h4>
                    <p className="text-sm text-gray-600" data-id="element-1916">Auftrag #{order.id}</p>
                  </div>
                  <div data-id="element-1917">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                      ${order.status === 'in-progress' ? 'bg-blue-100 text-blue-800' : order.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-1918">
                      {order.status === 'in-progress' ? 'In Bearbeitung' : order.status === 'completed' ? 'Abgeschlossen' : 'Geplant'}
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-1919">
                  <h5 className="font-medium text-gray-700 mb-3 flex items-center" data-id="element-1920">
                    <PackageIcon size={16} className="mr-2" data-id="element-1921" />
                    Komponenten im TK-Lager
                  </h5>
                  <div className="overflow-x-auto" data-id="element-1922">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-1923">
                      <thead className="bg-gray-50" data-id="element-1924">
                        <tr data-id="element-1925">
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1926">
                            Material
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1927">
                            Menge
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1928">
                            Lagerplatz
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1929">
                            Aktion
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-1930">
                        {order.components.map(component => <tr key={component.id} className="hover:bg-gray-50" data-id="element-1931">
                            <td className="px-4 py-4 whitespace-nowrap" data-id="element-1932">
                              <div data-id="element-1933">
                                <div className="font-medium text-gray-900" data-id="element-1934">
                                  {component.name}
                                </div>
                                <div className="text-sm text-gray-500" data-id="element-1935">
                                  {component.id}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap" data-id="element-1936">
                              <div className="text-sm text-gray-900" data-id="element-1937">
                                {component.quantity} {component.unit}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap" data-id="element-1938">
                              <div className="flex items-center" data-id="element-1939">
                                <MapPinIcon size={16} className="text-gray-400 mr-1" data-id="element-1940" />
                                <div className="text-sm text-gray-900" data-id="element-1941">
                                  {component.location}{' '}
                                  <span className="text-gray-500" data-id="element-1942">
                                    ({component.position})
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-right" data-id="element-1943">
                              <button onClick={() => handleShowLocation(component, order.id)} className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-md text-sm" data-id="element-1944">
                                Auslagern
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>)}
          </div> : <div className="text-center py-10 bg-gray-50 rounded-lg" data-id="element-1945">
            <WarehouseIcon size={40} className="mx-auto text-gray-400 mb-3" data-id="element-1946" />
            <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-1947">
              Keine Aufträge gefunden
            </h3>
            <p className="text-gray-500" data-id="element-1948">
              Es wurden keine Aufträge mit Komponenten im TK-Lager gefunden, die
              Ihren Suchkriterien entsprechen.
            </p>
          </div>}
      </div>
      {/* Modal für die Lagerplatzanzeige und Bestätigung */}
      {showLocationModal && selectedComponent && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1949">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full mx-4" data-id="element-1950">
            <div className="flex justify-between items-start mb-4" data-id="element-1951">
              <h3 className="text-xl font-bold text-gray-800" data-id="element-1952">
                Auslagerung bestätigen
              </h3>
              <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700" data-id="element-1953">
                <XIcon size={20} data-id="element-1954" />
              </button>
            </div>
            <div className="mb-4" data-id="element-1955">
              <h4 className="font-medium text-gray-700 mb-2" data-id="element-1956">
                {selectedComponent.name}
              </h4>
              <p className="text-sm text-gray-600 mb-1" data-id="element-1957">
                Artikel-Nr: {selectedComponent.id}
              </p>
              <p className="text-sm text-gray-600 mb-3" data-id="element-1958">
                Menge: {selectedComponent.quantity} {selectedComponent.unit}
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4" data-id="element-1959">
                <h5 className="font-medium text-amber-800 mb-2 flex items-center" data-id="element-1960">
                  <MapPinIcon size={16} className="mr-2" data-id="element-1961" />
                  Lagerplatz: {selectedComponent.location}
                </h5>
                <p className="text-sm text-amber-700 mb-2" data-id="element-1962">
                  Position:{' '}
                  <span className="font-semibold" data-id="element-1963">
                    {selectedComponent.position}
                  </span>
                </p>
                {/* Erläuterung der Koordinaten */}

                {/* Visuelle Darstellung des Lagerplatzes */}
                <div className="bg-white border border-gray-200 rounded-lg p-3" data-id="element-1964">
                  <div className="text-sm font-medium text-gray-700 mb-2" data-id="element-1965">
                    Lagerplatz-Visualisierung:
                  </div>
                  {renderStorageGrid(selectedComponent.position)}
                </div>
              </div>
            </div>
            <div className="flex justify-between gap-4" data-id="element-1966">
              <button onClick={handleCloseModal} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md flex items-center justify-center gap-2" data-id="element-1967">
                <XIcon size={16} data-id="element-1968" />
                <span data-id="element-1969">Abbrechen</span>
              </button>
              <button onClick={handleStartScan} className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-md flex items-center justify-center gap-2" data-id="element-1970">
                <CheckIcon size={16} data-id="element-1971" />
                <span data-id="element-1972">Auslagerung starten</span>
              </button>
            </div>
          </div>
        </div>}
      {/* Modal für die Barcode-Validierung */}
      {showScanModal && selectedComponent && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1973">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full mx-4" data-id="element-1974">
            <div className="flex justify-between items-start mb-4" data-id="element-1975">
              <h3 className="text-xl font-bold text-gray-800" data-id="element-1976">
                Material scannen
              </h3>
              <button onClick={handleCloseScanModal} className="text-gray-500 hover:text-gray-700" data-id="element-1977">
                <XIcon size={20} data-id="element-1978" />
              </button>
            </div>
            <div className="mb-6 text-center" data-id="element-1979">
              <div className="bg-amber-100 p-3 rounded-full inline-flex mb-4" data-id="element-1980">
                <ScanLineIcon size={32} className="text-amber-600" data-id="element-1981" />
              </div>
              <h4 className="font-medium text-gray-800 mb-2" data-id="element-1982">
                Bitte scannen Sie das Material zur Bestätigung
              </h4>
              <p className="text-sm text-gray-600 mb-4" data-id="element-1983">
                Scannen Sie den Barcode auf {selectedComponent.name}{' '}
                (Artikel-Nr: {selectedComponent.id}) oder den Lagerplatz{' '}
                {selectedComponent.location} um zu bestätigen, dass Sie das
                richtige Material auslagern.
              </p>
              <div className="mb-4" data-id="element-1984">
                <div className="flex gap-2" data-id="element-1985">
                  <div className="flex-1 relative" data-id="element-1986">
                    <input type="text" className={`block w-full pl-10 pr-3 py-3 border ${scanSuccess === true ? 'border-green-500 bg-green-50' : scanSuccess === false ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500`} placeholder="Barcode scannen oder eingeben..." value={scannedCode} onChange={e => setScannedCode(e.target.value)} onKeyDown={handleKeyDown} autoFocus data-id="element-1987" />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1988">
                      <ScanLineIcon size={20} className="text-gray-400" data-id="element-1989" />
                    </div>
                  </div>
                  <button onClick={handleScan} disabled={isScanning} className={`
                      bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded flex items-center justify-center
                      ${isScanning ? 'opacity-70 cursor-not-allowed' : ''}
                    `} data-id="element-1990">
                    <span data-id="element-1991">{isScanning ? 'Scanne...' : 'Scannen'}</span>
                  </button>
                </div>
                {scanSuccess === true && <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center" data-id="element-1992">
                    <CheckCircleIcon size={18} className="text-green-600 mr-2" data-id="element-1993" />
                    <span className="text-green-700" data-id="element-1994">
                      {showSuccessMessage ? 'Auslagerung erfolgreich! Sie werden zur Übersicht zurückgeleitet...' : 'Material erfolgreich validiert!'}
                    </span>
                  </div>}
                {scanSuccess === false && <div className="mt-4 flex items-center text-red-600" data-id="element-1995">
                    <AlertCircleIcon size={16} className="mr-1" data-id="element-1996" />
                    <span data-id="element-1997">{errorMessage}</span>
                  </div>}
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-left text-sm text-gray-600" data-id="element-1998">
                <p className="font-medium text-gray-700 mb-1" data-id="element-1999">Hinweis:</p>
                <p data-id="element-2000">
                  Für Testzwecke können Sie die ersten 3 Zeichen der Artikel-Nr.
                  ({selectedComponent.id.substring(0, 3)}) oder den Lagerplatz (
                  {selectedComponent.location}) eingeben.
                </p>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}